import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { searchAppointments } from '../../../../../store/calendarService';
import { createPoolApplication } from '../../../../../store/recruitmentService';
import { countDays } from '../../../../../utils/dateFunctions';
import { clearKeys, trimEntries } from '../../../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import SingleSelect from '../../../../formElements/SingleSelect';
import FormActions from '../../../../ui/formActions/FormActions';

import SelectPools from './SelectPools';
import { validate } from './validateApplyToPoolEditor';

import form from '../../../../../commonStyles/formStyles.module.css';
import classes from '../../submissionStyles/pools.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const ApplyToPoolEditor = ({ isOpen, onClosePoolEditor, participantDocuments, poolApplications }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        goldStandardCVId: null,
        participantId: null,
        participantPoolIds: null
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [keys, setKeys] = useState({ goldStandardCVId: '0' });
    const [errors, setErrors] = useState({});
    const [rows, setRows] = useState([]);
    const [jobSectorId, setJobSectorId] = useState('');
    const [hasMockInterview, setHasMockInterview] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const participantAppointments = useSelector(
        (state) => state.entities.calendarService.appointmentSearch
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantEmployability = useSelector(
        (state) => state.entities.participantService.currentParticipantEmployability
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
        dispatch(
            searchAppointments(
                {
                    participantIds: [currentParticipant.id],
                    types: ['Mock Interview'],
                    attendances: ['Attended']
                },
                0
            )
        );
    }, []);

    useEffect(() => {
        if (participantAppointments?.length < 1) setHasMockInterview(false);
        else {
            setHasMockInterview(
                participantAppointments
                    .map((el) => el.attendances)
                    .flat()
                    .some((el) => countDays(el.date, new Date()) < 31)
            );
        }
    }, [participantAppointments]);

    useEffect(() => {
        if (successMessage === `Pool application(s) have been added`) {
            setIsDisabled(false);
            clearData();
        }
    }, [successMessage]);

    useEffect(() => {
        setIsDisabled(!!Object.keys(errors)?.length);
    }, [errors]);

    // HELPER FNS
    const getPreSelect = (arr, id) => arr.find((el) => el.id === id) || {};

    const clearError = (key) => {
        setErrors(trimEntries([key], errors));
    };

    const clearData = () => {
        setErrors({});
        setRows([]);
        setJobSectorId('');
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        onClosePoolEditor();
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        errors[key] && clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onModalClose = (event, reason) => {
        if (reason !== 'backdropClick') {
            onClosePoolEditor();
        }
    };

    const onRowsUpdated = (updatedRows) => {
        clearError('participantPoolIds');
        setRows(updatedRows);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsDisabled(true);

        const participantPoolIds = rows.filter((el) => el.update === true).map((el) => el.id);

        const payload = {
            ...newEntry,
            participantId: currentParticipant.id,
            participantPoolIds,
            jobSectorId
        };
        const newErrors = validate(payload);

        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(createPoolApplication(payload));
    };

    // RENDER
    return (
        <div>
            <Dialog
                className="muiDialogWrapper"
                open={isOpen}
                onClose={onModalClose}
                aria-labelledby="form-dialog-title">
                <form
                    onSubmit={onSubmit}
                    data-testid="apply_pool_editor_form"
                    className={form.form}>
                    <DialogTitle id="form-dialog-title">Select Pool(s) For Application</DialogTitle>
                    <DialogContent data-testid="dialog-content">
                        <SingleSelect
                            id={'goldStandardCVId'}
                            key={keys.goldStandardCVId}
                            label={'Gold Standard CV'}
                            placeholder="Select Gold Standard CV..."
                            disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                            mandatory={true}
                            menuItems={participantDocuments}
                            selectedId={newEntry.goldStandardCVId}
                            selected={getPreSelect(participantDocuments, newEntry.goldStandardCVId)}
                            error={errors.goldStandardCVId}
                            onChange={(chosenId) => onUpdate('goldStandardCVId', chosenId)}
                        />
                        <h3>Criteria For Pool Applications:</h3>
                        <section>
                            {participantDocuments?.length < 1 ? (
                                <CancelIcon className={classes.red} />
                            ) : (
                                <CheckCircleIcon className={classes.green} />
                            )}{' '}
                            Have a Gold Standard CV attached to their record
                        </section>
                        <section>
                            {Object.keys(currentParticipantEmployability).length < 1 ||
                            currentParticipantEmployability?.rightToWorkDocumentTypeId === null ? (
                                <CancelIcon className={classes.red} />
                            ) : (
                                <CheckCircleIcon className={classes.green} />
                            )}{' '}
                            Right to Work Documents available
                        </section>
                        <section>
                            {hasMockInterview === false ? (
                                <CancelIcon className={classes.red} />
                            ) : (
                                <CheckCircleIcon className={classes.green} />
                            )}{' '}
                            Participant has attended at least one mock interview within last month
                        </section>
                        <p>Select a Pool to submit your Participant to for consideration</p>
                        <SelectPools
                            onRowsUpdated={onRowsUpdated}
                            poolApplications={poolApplications}
                            jobSectorId={jobSectorId}
                            onUpdateJobSectorId={setJobSectorId}
                            errors={errors}
                            clearError={clearError}
                        />
                    </DialogContent>

                    <DialogActions>
                        <FormActions
                            id="submit"
                            btnText="Update"
                            disabled={
                                !hasRole(acceptedRoles, loggedInUser.roles) ||
                                participantDocuments?.length < 1 ||
                                currentParticipantEmployability?.rightToWorkDocumentTypeId ===
                                    null ||
                                currentParticipantEmployability?.rightToWorkDocumentTypeId ===
                                    undefined ||
                                hasMockInterview === false ||
                                isDisabled
                            }
                            onClose={onSubmit}
                            onCancel={clearData}
                            customClass="noTopBorder"
                        />
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
};

ApplyToPoolEditor.propTypes = {
    onClosePoolEditor: PropTypes.func,
    isOpen: PropTypes.bool.isRequired,
    participantDocuments: PropTypes.array.isRequired,
    poolApplications: PropTypes.array
};

export default ApplyToPoolEditor;
